/**
 * Sudsy PDP — JavaScript module
 *
 * Handles:
 *  1. Direct-to-checkout: intercepts the native add-to-cart form submit
 *     and redirects to /checkout after a successful cart add.
 *  2. CTA buttons: [data-sudsy-cta] elements anywhere on the page
 *     add the current variant to cart once, then redirect to /checkout.
 *  3. Gallery click zones: a click on the outer edge of the hero image acts
 *     as a click on the arrow on that side.
 *  4. Gallery autoplay: a video slide starts playing once a user selects it.
 *  5. Gallery loop: a finished video fades to black, holds, and plays again;
 *     any video out of the user's view is paused.
 */

(function () {
  'use strict';

  /* ─── Utilities ─────────────────────────────────────────────────── */

  function getVariantId() {
    var input = document.querySelector('[data-type="add-to-cart-form"] input[name="id"]');
    return input ? parseInt(input.value, 10) : null;
  }

  function addToCartAndCheckout(btn) {
    if (btn && btn.dataset.adding) return;
    var id = getVariantId();
    if (!id) return;

    if (btn) {
      btn.dataset.adding = 'true';
      btn.dataset.originalText = btn.innerHTML;
      btn.innerHTML = 'Adding\u2026';
      btn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    }).then(function () {
      window.location.href = '/checkout';
    }).catch(function () {
      if (btn) {
        btn.innerHTML = btn.dataset.originalText;
        delete btn.dataset.adding;
        btn.disabled = false;
      }
    });
  }

  /* ─── 1. Hook the native add-to-cart form ───────────────────────── */

  function hookAddToCartForm() {
    var form = document.querySelector('[data-type="add-to-cart-form"]');
    if (!form || form._sudsyHooked) return;
    form._sudsyHooked = true;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      addToCartAndCheckout(null);
    }, true);
  }

  /* ─── 2. Delegate clicks on [data-sudsy-cta] buttons ───────────── */

  function hookCtaButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-sudsy-cta]');
      if (!btn) return;
      e.preventDefault();
      addToCartAndCheckout(btn);
    });
  }

  /* ─── 3. Gallery click zones ────────────────────────────────────── */

  /* The outer ZONE_FRACTION of the hero image, on each side, behaves like the
     arrow on that side. Horizon's gallery already has working arrows, so this
     does not reimplement navigation: it clicks the real arrow button, which
     keeps looping, transitions and every other listener identical to a click
     on the arrow itself.

     Only a click counts, never a drag or a swipe. Horizon's own drag handler
     suppresses nothing reliably once the pointer is released, so the decision
     is made here from what the press actually did:

       - the pointer travelled more than TAP_SLOP_PX between press and release
         (a mouse drag, or a swipe that began inside a zone), or
       - the page scrolled during the press (a vertical scroll that happened
         to start on the image: no angle math can outvote the browser having
         already decided it was a scroll), or
       - a second finger arrived (a pinch), or
       - there was no real press at all (an Enter keypress on a focused button
         also produces a click, with coordinates of 0,0: the left edge).

     The handler runs in the capture phase and stops the click. On a video slide
     the poster button, and once the video is loaded the play/pause toggle,
     covers the whole slide, so without that a click in a zone would load or
     pause the video instead of turning the slide. The centre keeps doing
     exactly what it did.

     Zones exist only where the arrows do. Below 750px Horizon hides them, and
     asking whether the arrow is rendered tracks that breakpoint without
     hardcoding it here. */

  var ZONE_FRACTION = 0.15;
  var TAP_SLOP_PX = 10;
  var SCROLL_SLOP_PX = 10;

  /* The PDP gallery's own slideshow for any node inside one of its slides.
     Dialogs are excluded: a zoom lightbox carries its own slideshow. */
  function gallerySlideshow(node) {
    var slide = node && node.closest ? node.closest('media-gallery slideshow-slide') : null;
    var slideshow = slide ? slide.closest('slideshow-component') : null;
    return slideshow && !slideshow.closest('dialog') ? slideshow : null;
  }

  function isRendered(el) {
    if (!el) return false;
    var box = el.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  /* 'previous' / 'next' plus the arrow to click, or null outside the zones or
     where the arrows are not shown. */
  function zoneAt(slideshow, clientX) {
    var frame = (slideshow.refs && slideshow.refs.slideshowContainer) || slideshow;
    var box = frame.getBoundingClientRect();
    if (!box.width) return null;

    var across = (clientX - box.left) / box.width;
    var name = across < ZONE_FRACTION ? 'previous' : across > 1 - ZONE_FRACTION ? 'next' : null;
    if (!name) return null;

    var arrow = slideshow.querySelector('.slideshow-control--' + name);
    return isRendered(arrow) ? { name: name, arrow: arrow } : null;
  }

  function hookGalleryZones() {
    var press = null;

    document.addEventListener('pointerdown', function (e) {
      /* Only the first finger or the mouse can start a tap, and only on a
         slide: the arrows and thumbnails handle their own clicks. */
      var slideshow = e.isPrimary ? gallerySlideshow(e.target) : null;
      press = slideshow
        ? { slideshow: slideshow, x: e.clientX, y: e.clientY, scrollY: window.scrollY }
        : null;
    }, true);

    document.addEventListener('pointercancel', function () {
      /* The browser took the gesture over to scroll or pinch. */
      press = null;
    }, true);

    document.addEventListener('click', function (e) {
      var start = press;
      press = null; /* consumed: the arrow click made below finds nothing */

      if (!start || !e.isTrusted || e.button !== 0) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

      /* The slideshow comes from where the press began, not from e.target. The
         moment the pointer moves at all, Horizon's drag handler captures it,
         and a captured pointer's click is retargeted to the slideshow element
         itself, which is not inside any slide. Reading e.target would drop
         every click that had even a pixel of mouse tremor. */
      var slideshow = start.slideshow;
      if (!slideshow.contains(e.target)) return;

      if (Math.abs(e.clientX - start.x) > TAP_SLOP_PX ||
          Math.abs(e.clientY - start.y) > TAP_SLOP_PX) return;
      if (Math.abs(window.scrollY - start.scrollY) > SCROLL_SLOP_PX) return;

      var zone = zoneAt(slideshow, e.clientX);
      if (!zone) return;

      e.preventDefault();
      e.stopPropagation();
      zone.arrow.click();
    }, true);

    /* Cursor cue. Hover only, so touch never sets it. CSS keys off the
       attribute; see "PDP gallery click zones" in sudsy-pdp.css. */
    var hovered = null;
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;

      var slideshow = gallerySlideshow(e.target);
      var zone = slideshow ? zoneAt(slideshow, e.clientX) : null;

      if (hovered && hovered !== slideshow) hovered.removeAttribute('data-sudsy-zone');
      if (slideshow) {
        if (zone) slideshow.setAttribute('data-sudsy-zone', zone.name);
        else slideshow.removeAttribute('data-sudsy-zone');
      }
      hovered = slideshow;
    }, { passive: true });
  }

  /* ─── 4. Gallery autoplay ───────────────────────────────────────── */

  /* Whenever a user lands on a slide that holds a video, play it, and pause the
     video they left. Two independent signals feed one step, arrive():

     1. Horizon's slideshow:select, which covers a thumbnail, an arrow, a zone
        click, a mouse drag and a touch swipe, and is the fast path.
     2. The scroller itself coming to rest on a different slide than it started
        on. Horizon does not always announce a swipe: after a touch tap its mouse
        drag handler is left half-open, and the next swipe can move the slide
        without ever dispatching slideshow:select (measured at baseline, with
        none of this code loaded). Taps before swipes are the normal pattern on
        a phone, so relying on the event alone made autoplay intermittent
        exactly where it matters.

     arrive() is idempotent per slide, so the two paths cannot double-fire, and
     the slower one cannot restart a video the user paused in between.

     The video template is muted, autoplay and playsinline, which browsers allow
     without a user gesture, so this works for a swipe as well as a click.

     Both paths wait AUTOPLAY_SETTLE_MS. A fast swipe across several slides
     crosses each one, and without the wait every video on the way would start
     loading. Leaving a slide pauses its video immediately.

     A select whose index equals its previousIndex is ignored. Horizon
     dispatches a 'drag' select on every mouse release, even a plain click that
     never moved, and treating that as a selection would restart a video the
     user had just paused by clicking it: the click pauses it, then this would
     play it again 150ms later.

     loadContent(false) rather than showDeferredMedia(): the latter focuses the
     new <video>, and focus() scrolls it into view, which would pull the page
     when the gallery is only partly on screen. */

  var AUTOPLAY_SETTLE_MS = 150;

  function playSlideMedia(slide) {
    var media = slide.querySelector('deferred-media');
    if (!media || typeof media.loadContent !== 'function' || media.isPlaying) return;

    if (!media.getAttribute('data-media-loaded')) {
      media.loadContent(false);
      if (!media.getAttribute('data-media-loaded')) return;
      media.isPlaying = true;
      media.updatePlayPauseHint(true);
    } else {
      media.playMedia();
    }

    /* Low Power Mode and some data-saver settings refuse even muted autoplay.
       Show the play icon instead of a pause icon over a still frame. */
    var video = media.querySelector('video');
    var attempt = video && video.play();
    if (attempt && attempt.catch) {
      attempt.catch(function () {
        media.isPlaying = false;
        media.updatePlayPauseHint(false);
      });
    }
  }

  function pauseOtherMedia(slideshow, slide) {
    slideshow.closest('media-gallery').querySelectorAll('deferred-media').forEach(function (media) {
      if (!slide.contains(media) && media.isPlaying) media.pauseMedia();
    });
  }

  /* The slide closest to the middle of the gallery. Geometry rather than
     slideshow.current, which lags during Horizon's long-jump reordering. */
  function centredSlide(slideshow) {
    var frame = (slideshow.refs && slideshow.refs.slideshowContainer) || slideshow;
    var box = frame.getBoundingClientRect();
    var middle = box.left + box.width / 2;
    var best = null;
    var bestGap = Infinity;

    (slideshow.slides || []).forEach(function (slide) {
      var rect = slide.getBoundingClientRect();
      var gap = Math.abs(rect.left + rect.width / 2 - middle);
      if (gap < bestGap) { best = slide; bestGap = gap; }
    });
    return best;
  }

  /* The slide each slideshow most recently arrived on. */
  var arrivedAt = new WeakMap();

  function arrive(slideshow, slide) {
    if (arrivedAt.get(slideshow) === slide) return;
    arrivedAt.set(slideshow, slide);
    pauseOtherMedia(slideshow, slide);
    playSlideMedia(slide);
  }

  function hookGalleryAutoplay() {
    var selectTimer = null;

    /* Signal 1: Horizon says a slide was selected. */
    document.addEventListener('slideshow:select', function (e) {
      var slide = e.detail && e.detail.slide;
      var slideshow = gallerySlideshow(slide);
      if (!slideshow || slideshow !== e.target) return;
      if (e.detail.index === e.detail.previousIndex) return;

      pauseOtherMedia(slideshow, slide);

      clearTimeout(selectTimer);
      if (!e.detail.userInitiated) return;

      selectTimer = setTimeout(function () {
        arrive(slideshow, slide);
      }, AUTOPLAY_SETTLE_MS);
    });

    /* Signal 2: the scroller came to rest on a different slide. scroll does
       not bubble, so listen in the capture phase. A burst is a run of scroll
       events with no gap longer than AUTOPLAY_SETTLE_MS; remember which slide it
       started on and compare when it ends. */
    var bursts = new WeakMap();

    document.addEventListener('scroll', function (e) {
      var scroller = e.target;
      if (!scroller || scroller.nodeType !== 1) return; /* the page itself */

      var slideshow = scroller.closest('slideshow-component');
      if (!slideshow || !slideshow.closest('media-gallery') || slideshow.closest('dialog')) return;
      if (!slideshow.refs || slideshow.refs.scroller !== scroller) return; /* the thumbnail strip */

      var burst = bursts.get(scroller);
      if (!burst) {
        burst = { from: centredSlide(slideshow), timer: 0 };
        bursts.set(scroller, burst);
      }

      clearTimeout(burst.timer);
      burst.timer = setTimeout(function () {
        bursts.delete(scroller);
        var slide = centredSlide(slideshow);
        if (slide && slide !== burst.from) arrive(slideshow, slide);
      }, AUTOPLAY_SETTLE_MS);
    }, { capture: true, passive: true });
  }

  /* ─── 5. Gallery loop and viewport ─────────────────────────────── */

  /* A finished video dips to black over its last FADE_OUT_MS, holds fully black
     for BLACK_HOLD_MS, then plays again, fading back in. Nothing here uses the
     <video loop> attribute: a native loop restarts on the next frame, so there
     is no moment to fade through. The clip's own events drive it instead.

     The black is an overlay element rather than a filter or an opacity on the
     video, so it does not depend on what sits behind the video, and the
     full-slide play/pause button above it keeps receiving clicks.

     The fade is scheduled, not polled. timeupdate only fires a few times a
     second, which can land well after the moment the fade should begin, so the
     start is computed from duration and currentTime and set as one timeout.
     It is recomputed on every play and seek. The fade is also capped to a share
     of the clip, because one of the product videos is only 2.4 seconds long.

     Resources. A video plays only while the gallery is in the user's view and
     the tab is visible. Leaving either pauses it; coming back resumes it, but
     only if this code was the one that paused it. A video the user paused
     stays paused, and so does one on a slide they have since left.

     The gallery is observed rather than the video. A slide still sliding in is
     mostly outside the scroller for a moment, and watching the video would
     read that as "out of view" and stutter every arrival. */

  var FADE_OUT_MS = 600;
  var BLACK_HOLD_MS = 400;
  var FADE_MAX_SHARE = 0.25;
  var IN_VIEW_BELOW = 0.25; /* pause under this share of the gallery visible */
  var IN_VIEW_ABOVE = 0.5;  /* resume at or over it; the gap stops flapping */

  var loopState = new WeakMap(); /* video -> { fadeTimer, holdTimer, resume } */
  var playedVideos = [];         /* every gallery video seen playing */
  var galleryInView = new WeakMap();
  var viewObserver = null;

  function galleryVideo(node) {
    return node && node.tagName === 'VIDEO' && node.closest('media-gallery deferred-media') ? node : null;
  }

  function stateOf(video) {
    var state = loopState.get(video);
    if (!state) {
      state = { fadeTimer: 0, holdTimer: 0, resume: false };
      loopState.set(video, state);
    }
    return state;
  }

  function fadeOverlay(video) {
    var media = video.closest('deferred-media');
    var overlay = media.querySelector('.sudsy-video-fade');
    if (!overlay) {
      overlay = document.createElement('span');
      overlay.className = 'sudsy-video-fade';
      overlay.setAttribute('aria-hidden', 'true');
      media.appendChild(overlay);
    }
    return overlay;
  }

  function isViewable(video) {
    var gallery = video.closest('media-gallery');
    return galleryInView.get(gallery) !== false && !document.hidden;
  }

  /* Schedule the fade to black so that it finishes on the last frame. */
  function armFade(video) {
    var state = stateOf(video);
    clearTimeout(state.fadeTimer);
    if (video.paused || video.ended || !isFinite(video.duration)) return;

    var fadeMs = Math.min(FADE_OUT_MS, video.duration * 1000 * FADE_MAX_SHARE);
    var untilFade = (video.duration - video.currentTime) * 1000 / (video.playbackRate || 1) - fadeMs;
    var overlay = fadeOverlay(video);

    overlay.style.setProperty('--sudsy-fade-out-ms', fadeMs + 'ms');
    if (untilFade <= 0) {
      overlay.classList.add('is-black');
    } else {
      state.fadeTimer = setTimeout(function () { overlay.classList.add('is-black'); }, untilFade);
    }
  }

  /* Play again from the start, if it is still wanted and still in view. */
  function replay(video) {
    var media = video.closest('deferred-media');
    if (!media || !media.isPlaying || !video.ended) return; /* paused, left, or already replaying */

    if (!isViewable(video)) {
      stateOf(video).resume = true; /* picked up when it comes back into view */
      return;
    }
    media.playMedia(); /* play() on an ended video restarts it from 0 */
  }

  /* Pause a playing video that has left the view; resume the ones this paused. */
  function syncViewable(video) {
    var state = stateOf(video);
    var media = video.closest('deferred-media');
    if (!media) return;

    if (!isViewable(video)) {
      if (!video.paused && media.isPlaying) {
        state.resume = true;
        media.pauseMedia();
      }
    } else if (state.resume) {
      state.resume = false;
      var slide = video.closest('slideshow-slide');
      var slideshow = gallerySlideshow(slide);
      /* Only the slide in front: a flag left by a slide the user has since
         swiped away from must not start its video behind their back. */
      if (slideshow && centredSlide(slideshow) === slide) media.playMedia();
    }
  }

  function observeGallery(video) {
    var gallery = video.closest('media-gallery');
    if (!gallery || galleryInView.has(gallery) || !window.IntersectionObserver) return;

    galleryInView.set(gallery, true);
    if (!viewObserver) {
      viewObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var was = galleryInView.get(entry.target);
          var now = was;
          if (entry.intersectionRatio < IN_VIEW_BELOW) now = false;
          else if (entry.intersectionRatio >= IN_VIEW_ABOVE) now = true;
          if (now === was) return;

          galleryInView.set(entry.target, now);
          playedVideos.forEach(syncViewable);
        });
      }, { threshold: [0, IN_VIEW_BELOW, IN_VIEW_ABOVE] });
    }
    viewObserver.observe(gallery);
  }

  function hookGalleryLoop() {
    /* Media events do not bubble, so listen in the capture phase. */
    document.addEventListener('playing', function (e) {
      var video = galleryVideo(e.target);
      if (!video) return;

      clearTimeout(stateOf(video).holdTimer);
      if (playedVideos.indexOf(video) === -1) playedVideos.push(video);
      observeGallery(video);

      fadeOverlay(video).classList.remove('is-black'); /* fades back in */
      armFade(video);
    }, true);

    document.addEventListener('seeked', function (e) {
      var video = galleryVideo(e.target);
      if (video) armFade(video);
    }, true);

    document.addEventListener('pause', function (e) {
      var video = galleryVideo(e.target);
      /* A video that ran to its end pauses itself just before 'ended'; that
         must keep its black frame, not clear it. */
      if (!video || video.ended || video.currentTime >= video.duration - 0.05) return;

      clearTimeout(stateOf(video).fadeTimer);
      fadeOverlay(video).classList.remove('is-black');
    }, true);

    document.addEventListener('ended', function (e) {
      var video = galleryVideo(e.target);
      if (!video) return;

      var state = stateOf(video);
      clearTimeout(state.fadeTimer);
      fadeOverlay(video).classList.add('is-black'); /* fully black, whatever the timer did */

      clearTimeout(state.holdTimer);
      state.holdTimer = setTimeout(function () { replay(video); }, BLACK_HOLD_MS);
    }, true);

    document.addEventListener('visibilitychange', function () {
      playedVideos.forEach(syncViewable);
    });
  }

  /* ─── Init ──────────────────────────────────────────────────────── */

  function init() {
    hookAddToCartForm();
    hookCtaButtons();
    hookGalleryZones();
    hookGalleryAutoplay();
    hookGalleryLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
